import { useMemo } from 'react';

import { DotsThreeVerticalIcon } from '@phosphor-icons/react/dist/ssr';

import SubscriptionAvatar from '@/shared/subscriptionAvatar';
import { TooltipWrapper } from '@/ui/TooltipWrapper';
import { useIsClient } from '@/utils/useIsClient';

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../ui/DropdownMenu';

import BadgeElement from '../shared/badgeElement/BadgeElement';
import { LoaderElement } from '../shared/loaderElement/LoaderElement';
import { SectionElement } from '../shared/sectionElement/SectionElement';
import type { AccountProfileElementProps } from './AccountProfile.types';

export function AccountProfileElement(props: AccountProfileElementProps) {
  const isClient = useIsClient();

  const {
    translations,
    children,
    canResetPassword,
    element,
    onClickEditProfile,
    onClickResetPassword,
    onClickLogout,
    onClickConnections,
    showActions,
    socialLoginProviders,
    isLoading,
  } = props;
  const { data } = element.attrs || {};

  const name = useMemo(() => {
    if (data?.firstName && data?.lastName) {
      return `${data.firstName} ${data.lastName}`;
    }

    if (data?.firstName) {
      return data?.firstName;
    }

    if (data?.lastName) {
      return data?.lastName;
    }

    return '-';
  }, [data?.firstName, data?.lastName]);

  if (isLoading) {
    return (
      <SectionElement title={translations?.['manage.account_profile.title']}>
        <LoaderElement count={4} />
      </SectionElement>
    );
  }

  const badgeElement = data?.emailChangeStatusBadge ? (
    <TooltipWrapper
      portalId="email-verification-pending-tooltip"
      tooltipText={translations?.['manage.update_profile_modal.form.email_change_helper_text'] || ''}
    >
      <BadgeElement className="w-fit h-fit" type={data.emailChangeStatusBadge.type}>
        {data.emailChangeStatusBadge.text}
      </BadgeElement>
    </TooltipWrapper>
  ) : null;

  return (
    <SectionElement
      title={translations?.['manage.account_profile.title']}
      subTitle={translations?.['manage.account_profile.description']}
      badgeElement={
        data?.emailChangeStatusBadge && <div className="w-full flex justify-end sm:hidden">{badgeElement}</div>
      }
      rightChildren={
        <div className="flex flex-row items-center gap-2">
          <div className="hidden sm:block">{badgeElement}</div>
          {showActions && isClient && (
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center justify-center w-8 h-8 bg-white border border-gray-200 rounded-md shadow-sm grow-1 shrink-0">
                <DotsThreeVerticalIcon width={16} height={16} className="color-gray-900" weight="bold" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onClickEditProfile}>
                  {translations?.['manage.account_profile.dropdown_options.edit_profile_label']}
                </DropdownMenuItem>
                {socialLoginProviders && socialLoginProviders.length > 0 && (
                  <DropdownMenuItem onClick={onClickConnections}>
                    {translations?.['manage.account_profile.dropdown_options.connections_label']}
                  </DropdownMenuItem>
                )}
                {canResetPassword && (
                  <DropdownMenuItem onClick={onClickResetPassword}>
                    {translations?.['manage.account_profile.dropdown_options.reset_password_label']}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={onClickLogout}>
                  {translations?.['manage.account_profile.dropdown_options.logout']}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      }
    >
      <div className="bg-[#F9FAFB] rounded-md p-4 flex items-center gap-6 border border-gray-200">
        <div className="w-[45px] h-[45px] grow-1 shrink-0 rounded-full bg-gradient-to-br from-gray-200 to-gray-400 flex items-center justify-center overflow-hidden">
          <SubscriptionAvatar avatarUrl={data?.avatarUrl} />
        </div>
        <div className="grid grid-cols-1 grid-rows-2 sm:grid-cols-[minmax(50px,_auto)_1fr] gap-1 sm:gap-x-2 sm:gap-y-1 w-full items-center">
          <span className="text-gray-500 text-xs leading-4">
            {translations?.['manage.account_profile.form.email_label']}
          </span>
          <span
            className="text-gray-500 text-sm leading-5 font-medium truncate max-w-[180px] block sm:max-w-full"
            title={data?.email}
          >
            {data?.email || '-'}
          </span>
          <span className="text-gray-500 text-xs leading-4">
            {translations?.['manage.account_profile.form.name_label']}
          </span>
          <span className="text-gray-500 text-sm leading-5 font-medium break-words whitespace-normal">{name}</span>
        </div>
      </div>
      {children}
    </SectionElement>
  );
}
